import { useState, useContext } from "react";
import {
  Form,
  Button,
  Card,
  Radio,
  Space,
  Row,
  Col,
  Input,
  Select,
  InputNumber,
  Typography,
} from "antd";
import {
  QuestionCircleTwoTone,
  EyeInvisibleOutlined,
  EyeTwoTone,
} from "@ant-design/icons";

import { SpecSettingContext } from "../../../pages/SpecSetting";
import { apiAuth, databaseType } from "../../../utils/blueprint";
import ConnectionStepDrawer from "./ConnectionStepDrawer";

import "./steps.css";

const { Title, Text } = Typography;
const { TextArea } = Input;

const ConnectionStep = ({ approval }) => {
  const { blueprint, updateSettingBlueprint } = useContext(SpecSettingContext);

  const [drawerOpen, setDrawerOpen] = useState(false);

  const [connectionForm] = Form.useForm();
  const connectionType = Form.useWatch("type", connectionForm);

  return (
    <div>
      <ConnectionStepDrawer open={drawerOpen} setOpen={setDrawerOpen} />
      <Title level={4}>
        連線方式配置
        <Button
          type="text"
          shape="circle"
          icon={<QuestionCircleTwoTone />}
          onClick={() => setDrawerOpen(true)}
        />
      </Title>
      <Text type="secondary">
        請選擇與客戶 ERP 系統的連線方式，並填入相應的連線參數。
      </Text>

      <Form
        form={connectionForm}
        disabled={approval}
        layout="vertical"
        initialValues={blueprint["connectionConfig"]}
        onValuesChange={(values) => {
          let newConfig;
          const type = Object.keys(values)[0];
          if (type === "type") {
            newConfig = {
              ...blueprint["connectionConfig"],
              type: values["type"],
            };
          } else {
            newConfig = {
              ...blueprint["connectionConfig"],
              [type]: {
                ...blueprint["connectionConfig"][type],
                ...values[type],
              },
            };
          }

          updateSettingBlueprint("connectionConfig", newConfig);
        }}
        style={{ marginTop: 24 }}
      >
        <Card>
          <Form.Item
            name="type"
            label="連線方式"
            rules={[{ required: true, message: "請選擇連線方式" }]}
          >
            <Radio.Group>
              <Space direction="vertical">
                <Radio value="database">
                  <strong>資料庫連線</strong>
                  <div className="option-description">
                    直接連接客戶的資料庫，適用於有資料庫存取權限的情況
                  </div>
                </Radio>
                <Radio value="api">
                  <strong>API 連線</strong>
                  <div className="option-description">
                    透過客戶提供的 API 介面存取數據，適用於有 RESTful API 的情況
                  </div>
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </Card>

        {connectionType === "database" && (
          <Card className="section-card" title="資料庫連線設定">
            <div className="information-block">
              <strong>💡 新 ERP 資料庫連線：</strong>此設定通常用於連接全新的
              ERP 系統資料庫。
              請確保資料庫已開放相應的連接埠，並提供具有讀取權限的專用帳號。
            </div>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={["database", "type"]}
                  label="資料庫類型"
                  rules={[{ required: true, message: "請選擇資料庫類型" }]}
                  extra="選擇客戶 ERP 系統使用的資料庫類型"
                >
                  <Select
                    placeholder="請選擇資料庫類型"
                    options={databaseType}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={["database", "host"]}
                  label="主機位址"
                  rules={[{ required: true, message: "請輸入主機位址" }]}
                  extra="ERP 資料庫伺服器的 IP 位址或域名"
                >
                  <Input placeholder="例如：192.168.1.100 或 erp-db.company.com" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={["database", "port"]}
                  label="連接埠"
                  rules={[{ required: true, message: "請輸入連接埠號" }]}
                  extra="資料庫服務的連接埠號"
                >
                  <InputNumber
                    placeholder="例如：3306"
                    min={1}
                    max={65535}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item
                  name={["database", "database"]}
                  label="資料庫名稱"
                  rules={[{ required: true, message: "請輸入資料庫名稱" }]}
                  extra="ERP 系統主要資料庫的名稱"
                >
                  <Input placeholder="例如：oracle_erp, sap_db, erp_main" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={["database", "username"]}
                  label="使用者名稱"
                  rules={[{ required: true, message: "請輸入使用者名稱" }]}
                  extra="建議使用具有唯讀權限的專用帳號"
                >
                  <Input placeholder="例如：erp_reader, sync_user" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={["database", "password"]}
                  label="密碼"
                  rules={[{ required: true, message: "請輸入密碼" }]}
                  extra="請確保密碼強度符合安全要求"
                >
                  <Input.Password
                    placeholder="輸入資料庫密碼"
                    iconRender={(visible) =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}

        {connectionType === "api" && (
          <Card className="section-card" title="API 連線設定">
            <Form.Item
              name={["api", "baseUrl"]}
              label="API 基礎網址"
              rules={[
                { required: true, message: "請輸入 API 基礎網址" },
                { type: "url", message: "請輸入有效的 URL" },
              ]}
            >
              <Input placeholder="例如：https://api.customer-erp.com" />
            </Form.Item>

            <Form.Item
              name={["api", "authentication", "type"]}
              label="認證方式"
              rules={[{ required: true, message: "請選擇認證方式" }]}
            >
              <Select placeholder="請選擇認證方式" options={apiAuth}></Select>
            </Form.Item>

            <Form.Item
              name={["api", "authentication", "credentials"]}
              label="認證憑證"
              rules={[{ required: true, message: "請輸入認證憑證" }]}
            >
              <TextArea
                rows={4}
                placeholder='請以 JSON 格式輸入認證資訊，例如：{"token": "your-api-token"}'
              />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name={["api", "timeout"]} label="超時時間（秒）">
                  <InputNumber
                    placeholder="30"
                    min={1}
                    max={300}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name={["api", "retryCount"]} label="重試次數">
                  <InputNumber
                    placeholder="3"
                    min={0}
                    max={10}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}
      </Form>
    </div>
  );
};

export default ConnectionStep;
